import * as TooltipPrimitive from "@radix-ui/react-tooltip";
import { ReactElement, ReactNode } from "react";
import { cx } from "../../utils/cx";
import styles from "./Tooltip.module.scss";

export type TooltipTone = "light" | "dark";
export type TooltipVariant = "single" | "multi";

export type TooltipProviderProps = TooltipPrimitive.TooltipProviderProps;

/**
 * Wraps the app (or Storybook) once. Figma: **300ms** delay before open.
 * `skipDelayDuration` defaults to Radix **300ms** (quick hop between triggers).
 */
export function TooltipProvider({
    delayDuration = 300,
    skipDelayDuration = 300,
    ...rest
}: TooltipProviderProps) {
    return (
        <TooltipPrimitive.Provider
            delayDuration={delayDuration}
            skipDelayDuration={skipDelayDuration}
            {...rest}
        />
    );
}

export type TooltipProps = {
    /** Single focusable / hoverable element (ref forwarded via `asChild`). */
    children: ReactElement;
    /** Tooltip body copy. */
    content: ReactNode;
    /** Optional title row (Figma “Multi rows + header”). */
    header?: ReactNode;
    /**
     * `single` → max-width **284px** (Figma single row).
     * `multi` → max-width **340px** with multiline body.
     */
    variant?: TooltipVariant;
    /**
     * `dark` = inverted black surface (**Figma default** when no art direction).
     * `light` = white surface + subtle border.
     */
    tone?: TooltipTone;
    /** Adds `cursor: help` on the trigger (Figma: badges and similar). */
    triggerHelpCursor?: boolean;
    /** Passed to `Tooltip.Root` — overrides `TooltipProvider` when set. */
    delayDuration?: number;
} & Pick<
    TooltipPrimitive.TooltipProps,
    "open" | "defaultOpen" | "onOpenChange" | "disableHoverableContent"
> &
    Pick<
        TooltipPrimitive.TooltipContentProps,
        | "side"
        | "align"
        | "sideOffset"
        | "alignOffset"
        | "className"
        | "aria-label"
    >;

/**
 * Treez DS — Tooltip
 *
 * Built on `@radix-ui/react-tooltip`. Visuals match Figma node `7335:50623`.
 * Prefer `tone="dark"` unless design specifies light.
 *
 * **Note:** Figma also describes **400ms** delay after pointer leaves; Radix
 * does not expose a hide delay — timing matches **open** delay only.
 */
export function Tooltip({
    children,
    content,
    header,
    variant = "single",
    tone = "dark",
    triggerHelpCursor = false,
    delayDuration = 300,
    open,
    defaultOpen,
    onOpenChange,
    disableHoverableContent,
    side = "top",
    align = "center",
    sideOffset = 8,
    alignOffset,
    className,
    "aria-label": ariaLabel,
}: TooltipProps) {
    const multiline = variant === "multi" || Boolean(header);

    return (
        <TooltipPrimitive.Provider
            delayDuration={delayDuration}
            skipDelayDuration={300}
        >
            <TooltipPrimitive.Root
                defaultOpen={defaultOpen}
                delayDuration={delayDuration}
                disableHoverableContent={disableHoverableContent}
                onOpenChange={onOpenChange}
                open={open}
            >
                <TooltipPrimitive.Trigger
                    asChild
                    className={
                        triggerHelpCursor ? styles.triggerHelp : undefined
                    }
                >
                    {children}
                </TooltipPrimitive.Trigger>
                <TooltipPrimitive.Portal>
                    <TooltipPrimitive.Content
                        align={align}
                        alignOffset={alignOffset}
                        aria-label={ariaLabel}
                        className={cx(
                            styles.content,
                            tone === "dark"
                                ? styles.toneDark
                                : styles.toneLight,
                            multiline
                                ? styles.layoutMultiline
                                : styles.layoutSingle,
                            className,
                        )}
                        data-ui="tooltip"
                        side={side}
                        sideOffset={sideOffset}
                    >
                        {header ? (
                            <div className={styles.header}>{header}</div>
                        ) : null}
                        <div
                            className={cx(
                                styles.body,
                                !multiline && styles.bodyMuted,
                            )}
                        >
                            {content}
                        </div>
                    </TooltipPrimitive.Content>
                </TooltipPrimitive.Portal>
            </TooltipPrimitive.Root>
        </TooltipPrimitive.Provider>
    );
}

export const TooltipRoot = TooltipPrimitive.Root;
export const TooltipTrigger = TooltipPrimitive.Trigger;
export const TooltipContent = TooltipPrimitive.Content;
export const TooltipPortal = TooltipPrimitive.Portal;
